import * as React from 'react'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/16/solid'
import { Button, ButtonText } from '@/components/ui/button'
import { Badge } from '@/components/ui/Badge'
import { UpgradeModal } from '@/components/feature-gate'
import type { MobileTable } from './useMobileTable'

function ModeButtons({ table }: { table: MobileTable }) {
  return (
    <div className="flex gap-1.5">
      <Button
        size="sm"
        className="h-8 rounded-lg px-3 text-sm"
        variant={table.isWeek ? 'default' : 'outline'}
        onClick={() => table.setMode('week')}
      >
        Week
      </Button>
      <Button
        size="sm"
        className="h-8 rounded-lg px-3 text-sm"
        variant={table.isWeek ? 'outline' : 'default'}
        onClick={() => table.setMode('month')}
      >
        Month
      </Button>
    </div>
  )
}

function TodayLink({ table }: { table: MobileTable }) {
  return (
    <button
      type="button"
      className="text-[12.5px] font-semibold text-txt-accent"
      onClick={table.goToday}
    >
      Today
    </button>
  )
}

function HistoryUpgrade({ table }: { table: MobileTable }) {
  return (
    <UpgradeModal
      feature={{ lookupKey: 'history.months', name: 'Longer history' }}
      open={table.historyUpgradeOpen}
      onOpenChange={table.setHistoryUpgradeOpen}
    />
  )
}

/** Phone: Week/Month + Today, then a ‹ period › pager. */
export function PhonePeriodControls({ table }: { table: MobileTable }) {
  const head = table.heads[0]
  return (
    <>
      <div className="flex items-center gap-2">
        <ModeButtons table={table} />
        <div className="ml-auto">
          <TodayLink table={table} />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          iconOnly
          aria-label="Previous period"
          className="size-9 rounded-xl"
          disabled={!table.canPrev}
          onClick={table.prev}
        >
          <ChevronLeftIcon />
        </Button>
        <div className="flex h-9 flex-1 items-center justify-center gap-2 rounded-xl bg-accent-tr-08 text-[13.5px] font-semibold text-txt-accent">
          {head?.sub ?? ''}
          {head?.isNow && <Badge className="rounded-md">NOW</Badge>}
        </div>
        <Button
          variant="outline"
          iconOnly
          aria-label="Next period"
          className="size-9 rounded-xl"
          disabled={!table.canNext}
          onClick={table.next}
        >
          <ChevronRightIcon />
        </Button>
      </div>
      <HistoryUpgrade table={table} />
    </>
  )
}

/** Tablet: one toolbar row; the vault scope button sits at the right end. */
export function TabletPeriodControls({
  table,
  children,
}: {
  table: MobileTable
  children?: React.ReactNode
}) {
  const first = table.heads[0]
  const last = table.heads[table.heads.length - 1]
  return (
    <div className="flex shrink-0 items-center gap-2.5 border-b border-primary bg-layout-secondary px-3 py-2">
      <ModeButtons table={table} />
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          iconOnly
          aria-label="Previous period"
          className="size-8 rounded-lg"
          disabled={!table.canPrev}
          onClick={table.prev}
        >
          <ChevronLeftIcon />
        </Button>
        <ButtonText className="min-w-[150px] text-center text-[13px] font-semibold text-txt-primary">
          {first && last
            ? first.offset === last.offset
              ? first.sub
              : `${first.label} – ${last.label}`
            : ''}
        </ButtonText>
        <Button
          variant="outline"
          size="sm"
          iconOnly
          aria-label="Next period"
          className="size-8 rounded-lg"
          disabled={!table.canNext}
          onClick={table.next}
        >
          <ChevronRightIcon />
        </Button>
      </div>
      <TodayLink table={table} />
      <div className="ml-auto">{children}</div>
      <HistoryUpgrade table={table} />
    </div>
  )
}
